import cx from 'classnames';
import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import ArrowLeftIcon from '../SVGIcons/ArrowLeftIcon';
import classes from './PaginationCatalog.module.scss';
import {
  getPaginationList,
  PaginationCatalogProps,
} from './PaginationCatalog.types';

interface PaginationProps extends PaginationCatalogProps {
  className?: string;
  setPageNumber: (n: number | string) => void;
}

export const PaginationCatalog: FC<PaginationProps> = ({
  className,
  currentPage,
  totalPages,
  showPagesNearby = 1,
  simbol,
  setPageNumber,
}): ReactElement => {
  const [currentPagePagination, setCurrentPagePagination] = useState(currentPage);

  useEffect(() => {
    setCurrentPagePagination(currentPage);
  }, [currentPage]);

  const onClick = useCallback(
    (page: number | string) => {
      setPageNumber(page);
    },
    [setPageNumber],
  );

  const paginationList = useMemo(
    () => getPaginationList({
      currentPage: currentPagePagination,
      totalPages,
      showPagesNearby,
      simbol,
    }),
    [currentPagePagination, totalPages, showPagesNearby, simbol],
  );

  const onPrevPage = () => {
    onClick(currentPage - 1);
  };

  const onNextPage = () => {
    onClick(currentPage + 1);
  };

  return totalPages > 1 ? (
    <div className={cx(classes['pagination-catalog'], className)}>
      <button
        type="button"
        onClick={onPrevPage}
        className={cx(
          classes.item,
          classes['item_is-number'],
          classes['pagination-catalog-button'],
          classes['pagination-catalog-button__prev'],
          {
            [classes['pagination-catalog-button-disabled']]: currentPage === 1,
            [classes['pagination-catalog-button-active']]: currentPage !== 1,
          },
        )}
        disabled={currentPage === 1}
      >
        <ArrowLeftIcon
          width="24"
          height="24"
          className={classes['pagination-catalog-chevron-icon']}
          classNamePatch={cx(
            classes['pagination-catalog-chevron-icon__patch'],
            {
              [classes['pagination-catalog-chevron-icon__patch-disabled']]:
                currentPage === 1,
              [classes['pagination-catalog-chevron-icon__patch-active']]:
                currentPage !== 1,
            },
          )}
        />
      </button>
      <div className={cx(classes['pagination-catalog'])}>
        {paginationList.map((item, index) => (
          <button
            type="button"
            className={cx(
              classes.item,
              { [classes['item_is-number']]: typeof item === 'number' },
              {
                [classes['item_is-number_active']]:
                  currentPagePagination === item,
              },
            )}
            onClick={() => item !== currentPage && onClick(item)}
            disabled={typeof item !== 'number'}
            key={`item_is-number-${String(index)}`}
          >
            <p className={classes['item-text']}>{item.toString()}</p>
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onNextPage}
        className={cx(
          classes.item,
          classes['pagination-catalog-button'],
          classes['item_is-number'],
          {
            [classes['pagination-catalog-button-disabled']]:
              currentPage === totalPages,
          },
          {
            [classes['pagination-catalog-button-active']]:
              currentPage !== totalPages,
          },
        )}
        disabled={currentPage === totalPages}
      >
        <ArrowLeftIcon
          width="24"
          height="24"
          className={cx(
            classes['pagination-catalog-chevron-icon'],
            classes['pagination-catalog-chevron-icon__right'],
          )}
          classNamePatch={cx(
            classes['pagination-catalog-chevron-icon__patch'],
            {
              [classes['pagination-catalog-chevron-icon__patch-disabled']]:
                currentPage === totalPages,
              [classes['pagination-catalog-chevron-icon__patch-active']]:
                currentPage !== totalPages,
            },
          )}
        />
      </button>
    </div>
  ) : (
    <div />
  );
};
